import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import List from "@material-ui/core/List";
import { MessageCard } from "../../MessageCard";
import { ListItem, Button } from "@material-ui/core";
import { PowUserResponse, AuditActionType, EditPowUserRoleRequest } from "../../../models/Administration";
import { UserProductDisplayCard } from "./UserProductDisplayCard";
import { UserProductRoleDisplayCard } from "./UserProductRoleDisplayCard";
import { PowFeatureDisplayCard } from "./PowFeatureDisplayCard";
import { Prompt } from "react-router-dom";
import { isEmptyObject } from "../../../utils/index";

interface UserProductSelectorProps {
	//User details to be used to tell if user already has product or role.
	userDetails: PowUserResponse;
	//The products to be displayed as well as the productRoles and Product Features in each product.
	products: object;
	//This method submits all of the changes that will be applied to the user in reguarding their productRole permissions.
	submitChanges: (requests: EditPowUserRoleRequest[]) => void;
	// Checks if changes have been made and enables page blocking on navigation
	isBlockingNavigation: (isBlockingNavigation: boolean) => boolean;
}

const useStyles = makeStyles((theme) => ({
	listContainer: {
		maxHeight: 500,
		overflow: "auto",
	},
	list: {
		maxWidth: theme.breakpoints.values.md,
		width: "100%",
	},
}));

/**
 * Fetch and display all pow users in the system in a list
 */
export const UserProductRoleEditor = (props: UserProductSelectorProps) => {
	const classes = useStyles();
	const [roles, setRoles] = React.useState({});
	const [editedProducts, setEditedProducts] = React.useState({});
	const [selectedProduct, setSelectedProduct] = React.useState(Object.keys(props.products)[0] || "");
	const [selectedProductRole, setSelectedProductRole] = React.useState(
		Object.keys(props.products[selectedProduct]["powRoles"])[0] || "",
	);

	function setSelectedProductName(productName: string) {
		if (productName !== selectedProduct) {
			setSelectedProduct(productName);
			setSelectedProductRole("");
		}
	}

	function submit() {
		props.submitChanges(
			Object.keys(roles).map((roleRequest) => ({
				actionType: roles[roleRequest],
				productRoleId: roleRequest,
			})),
		);
	}

	function editRoles(productRoleId: string) {
		const roleObject = { ...roles };
		const editedProductsObject = { ...editedProducts };
		if (roleObject[productRoleId] !== undefined) {
			delete roleObject[productRoleId];
			editedProductsObject[selectedProduct] = editedProductsObject[selectedProduct] - 1;
			if (editedProductsObject[selectedProduct] <= 0) {
				delete editedProductsObject[selectedProduct];
			}
		} else {
			if (editedProductsObject[selectedProduct] === undefined) {
				editedProductsObject[selectedProduct] = 1;
			} else {
				editedProductsObject[selectedProduct] = editedProductsObject[selectedProduct] + 1;
			}
			if (props.userDetails.powRoles[productRoleId] !== undefined) {
				roleObject[productRoleId] = AuditActionType.RemoveRole;
			} else {
				roleObject[productRoleId] = AuditActionType.AddRole;
			}
		}
		setRoles(roleObject);
		setEditedProducts(editedProductsObject);
	}

	return (
		<div>
			<div style={{ display: "flex", width: "100%" }}>
				<span data-testid="user-product-list" style={{ width: "32%", justifyContent: "center" }}>
					<h4 style={{ textAlign: "center" }}>Products</h4>
					<div>
						{props.products && Object.keys(props.products).length > 0 ? (
							<div className={classes.listContainer}>
								<List className={classes.list}>
									{Object.keys(props.products).map((productName) => (
										<ListItem key={productName} data-testid="user-list-item">
											<UserProductDisplayCard
												product={props.products[productName]}
												selected={productName === selectedProduct}
												edited={editedProducts[productName] !== undefined}
												setSelectedProduct={setSelectedProductName}
												enabled={props.userDetails.products[productName] !== undefined}
											></UserProductDisplayCard>
										</ListItem>
									))}
								</List>
							</div>
						) : (
							<MessageCard message="No products found." textAlign="left" />
						)}
					</div>
				</span>
				{selectedProduct !== "" && (
					<span style={{ width: "32%", justifyContent: "center" }}>
						<h4 style={{ textAlign: "center" }}>Product Roles</h4>
						{Object.keys(props.products[selectedProduct]["powRoles"]).length > 0 ? (
							<div className={classes.listContainer}>
								<List className={classes.list}>
									{Object.keys(props.products[selectedProduct]["powRoles"]).map((roleId) => (
										<ListItem key={roleId} data-testid="product-role-list-item">
											<UserProductRoleDisplayCard
												productRole={props.products[selectedProduct]["powRoles"][roleId]}
												edited={roles[roleId] !== undefined}
												enabled={
													(props.userDetails.powRoles[roleId] !== undefined &&
														roles[roleId] !== AuditActionType.RemoveRole) ||
													roles[roleId] === AuditActionType.AddRole
												}
												editRoles={editRoles}
												selected={selectedProductRole === roleId}
												setSelectedProductRole={setSelectedProductRole}
											></UserProductRoleDisplayCard>
										</ListItem>
									))}
								</List>
							</div>
						) : (
							<MessageCard message="No roles found for the product." textAlign="left" />
						)}
					</span>
				)}
				{selectedProductRole !== "" && (
					<span style={{ width: "32%", justifyContent: "center" }}>
						<h4 style={{ textAlign: "center" }}>Product Role Features</h4>
						{Object.keys(props.products[selectedProduct]["powRoles"][selectedProductRole]["powFeatures"]).length > 0 ? (
							<div className={classes.listContainer}>
								<List className={classes.list}>
									{props.products[selectedProduct]["powRoles"][selectedProductRole]["powFeatures"].map((feature) => (
										<ListItem key={feature["powFeatureName"]} data-testid="product-role-feature-list-item">
											<PowFeatureDisplayCard feature={feature}></PowFeatureDisplayCard>
										</ListItem>
									))}
								</List>
							</div>
						) : (
							<MessageCard message="No roles found for the product." textAlign="left" />
						)}
					</span>
				)}
			</div>
			<div
				style={{
					display: "flex",
					justifyContent: "center",
					paddingRight: 75,
					marginBottom: "50px",
				}}
				data-testid="submit-changes-button"
			>
				<Button
					style={{ marginBottom: "50px" }}
					size="large"
					variant="contained"
					color="primary"
					onClick={() => submit()}
				>
					Submit Changes
				</Button>
				<Prompt
					when={props.isBlockingNavigation(!isEmptyObject(editedProducts))}
					message="You have unsaved changes, are you sure you want to leave?"
				/>
			</div>
		</div>
	);
};
